import React, { Component } from "reactn";
import styled from "styled-components";
import { isDevelopment } from "js/config";
import { app } from "js/namespaces";
import Loadable from "js/react/components/Loadable";
import {
    UIPane,
    UIPaneContents,
    UIPaneHeader,
    Section, BlueButton,
} from "js/react/components/UiComponents";
import { ShowErrorDialog } from "js/react/components/Dialogs/BaseDialog";
import Api from "js/core/api";
import isOrganizationSsoEnabled from "common/utils/isOrganizationSsoEnabled";
import LogsViewer from "js/react/views/UserOptions/panes/LogsViewer";
import { BlueSwitch } from "js/react/components/Switch";

const SSOSpan = styled.div`
  color: rgba(255, 255, 255, 0.66);
  margin-bottom: 20px;
`;

const SSOContainer = styled.div`
  padding: 10px 0;
`;

const SSOStatusText = styled.p`
  margin-bottom: 10px;
`;

export default class AdvancedSettingPane extends Component {
    initialState = {
        authUser: app.user.getAuthUser(),
        isOrganizationFetching: true,
        //once access to sso feature is enabled this flag used to determine if sso is enabled
        isSsoEnabled: false,
        organization: {},
        isStrictSSOEnabled: false,
    };

    state = { ...this.initialState };

    async componentDidMount() {
        await this.loadData();
    }

    async componentDidUpdate(prevProps) {
        if (prevProps.organizationId !== this.props.organizationId) {
            this.setState(this.initialState, async () => {
                await this.loadData();
            });
        }
    }

    loadData() {
        const { organizationId } = this.props;
        Api.sso.get({ id: organizationId })
            .then(organization => {
                this.setStateFromOrganization(organization);
                this.setState({ isOrganizationFetching: false });
            })
            .catch(err => {
                console.error(err);

                ShowErrorDialog({
                    error: "Error",
                    message: "Failed to load your organization. Please email support@beautiful.ai for assistance."
                });
            });
    }

    /**
     * Converts an organization model object to a plain state object
     */
    setStateFromOrganization(organization) {
        this.setState({
            organization,
            isSsoEnabled: isOrganizationSsoEnabled(organization),
            isStrictSSOEnabled: isOrganizationSsoEnabled(organization) && organization.sso.strict
        });
    }

    renderSSOStatus() {
        const { isSsoEnabled, isStrictSSOEnabled } = this.state;
        if (isSsoEnabled) {
            return `SSO is enabled and ${isStrictSSOEnabled ? `required` : `optional` } for your organization.`;
        }
        return "SSO is not yet enabled for your organization.";
    }

    handleContentDistributionChange = stateDelta => {
        app.appState.currentTeam.update({ workspaceSettings: stateDelta });
        this.forceUpdate();
    }

    render() {
        const {
            isOrganizationFetching,
            isSsoEnabled,
        } = this.state;
        const {
            handleSSOConfigClicked,
            organizationId,
        } = this.props;

        // We don't call app.user.features.isFeatureEnabled() here because there's a
        //   delay due to the update needing to be loaded and formatted before the
        //   changes are reflected. So we access the appState directly instead.
        const workspaceSettings = app.appState.currentTeam.get("workspaceSettings") || {};
        const {
            prohibitExternalWorkspaceMovement,
            // NOTE: Hidden until google account linking no longer has loopholes across workspaces
            // prohibitGoogleDriveAccountLinking,
            prohibitExternalWorkspaceCollaboration,
        } = workspaceSettings;

        return (
            <UIPane>
                <UIPaneHeader>Advanced Settings</UIPaneHeader>
                <UIPaneContents>
                    <Loadable isLoading={isOrganizationFetching}>
                        <Section title="Content Distribution Settings">
                            <BlueSwitch
                                id="prohibitExternalWorkspaceMovement"
                                checked={prohibitExternalWorkspaceMovement}
                                onChange={() => this.handleContentDistributionChange({ prohibitExternalWorkspaceMovement: !prohibitExternalWorkspaceMovement })}
                                label="Prohibit moving presentations from this workspace"
                                tooltipTitle="Your team's presentations cannot be transferred to other workspaces, including Personal."
                            />
                            {/*
                            Hiding this setting until we determine a more robust way to lock down google accout linking.
                              Currently if a user links their account on their personal workspace, it also be linked in other
                              workspaces, regardless of this setting. This loophole largely makes this setting pointless.

                            <BlueSwitch
                                id="prohibitGoogleDriveAccountLinking"
                                checked={prohibitGoogleDriveAccountLinking}
                                onChange={() => this.handleContentDistributionChange({ prohibitGoogleDriveAccountLinking: !prohibitGoogleDriveAccountLinking })}
                                label="Prohibit account linking to Google Drive"
                                tooltipTitle="Disable this integration for users in this workspace and keep your team from syncing their presentations with Google Drive accounts."
                            />
                             */}
                            <BlueSwitch
                                id="prohibitExternalWorkspaceCollaboration"
                                checked={prohibitExternalWorkspaceCollaboration}
                                onChange={() => this.handleContentDistributionChange({ prohibitExternalWorkspaceCollaboration: !prohibitExternalWorkspaceCollaboration })}
                                label="Prohibit collaboration with users outside this workspace"
                                tooltipTitle="Team Members can only invite team members to collaborate on presentations within this workspace."
                            />
                        </Section>
                        <Section title="Single Sign-On">
                            <SSOSpan>Allow team members to login using a third-party identity provider</SSOSpan>
                            <SSOContainer>
                                <SSOStatusText>{this.renderSSOStatus()}</SSOStatusText>
                                <BlueButton onClick={handleSSOConfigClicked}>
                                    {isSsoEnabled ? "Edit Configuration" : "Enable SSO"}
                                </BlueButton>
                            </SSOContainer>
                        </Section>
                    </Loadable>
                    <LogsViewer organizationId={organizationId} />
                </UIPaneContents>
            </UIPane>
        );
    }
}
